import * as React from 'react';

import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';

export interface ConfirmDialogProps {
  /** Label for the cancel/dismiss button */
  cancelLabel: string;
  /** Extra content rendered between the description and the footer (e.g. a checkbox row) */
  children?: React.ReactNode;
  /** Disables only the confirm button (e.g. a typed-confirmation value that doesn't match yet) */
  confirmDisabled?: boolean;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Body copy explaining the consequences of the action */
  description: React.ReactNode;
  /** Renders the confirm button with the destructive button variant */
  destructive?: boolean;
  /** Whether the confirm action is in-flight. Disables both buttons. */
  isPending?: boolean;
  /** Called when the confirm button is clicked */
  onConfirm: () => void;
  /** Called when the dialog open state changes (dismiss, escape, outside click) */
  onOpenChange: (open: boolean) => void;
  open: boolean;
  title: React.ReactNode;
}

export function ConfirmDialog({
  cancelLabel,
  children,
  confirmDisabled = false,
  confirmLabel,
  description,
  destructive = false,
  isPending = false,
  onConfirm,
  onOpenChange,
  open,
  title,
}: ConfirmDialogProps): React.JSX.Element {
  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(destructive && buttonVariants({ variant: 'destructive' }))}
            disabled={isPending || confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
